import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Clock, ShieldCheck } from 'lucide-react';
import { BARANGAY, BRAND } from '@/data/mockData';

export const Footer = () => {
  const year = new Date().getFullYear();
  return (
    <footer className="relative mt-20 bg-primary-deep text-primary-foreground" data-testid="public-footer">
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-accent" />
      <div className="container-app pt-16 pb-8">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3">
              <img src={BRAND.logoUrl} alt="logo" className="h-14 w-14 rounded-full bg-white/95 p-0.5" />
              <div>
                <div className="font-display text-lg font-bold">{BARANGAY.name}</div>
                <div className="text-xs uppercase tracking-[0.18em] opacity-75">
                  {BARANGAY.municipality}, {BARANGAY.province}
                </div>
              </div>
            </div>
            <p className="mt-5 text-sm/relaxed opacity-85 max-w-sm">
              The Smart Barangay Action Center is the official digital home of {BARANGAY.name}. Built for transparent governance and faster service delivery.
            </p>
            <a
              href={BARANGAY.facebook}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 mt-5 rounded-full bg-white/10 px-4 py-2 text-sm font-medium hover:bg-white/15 transition"
              data-testid="footer-facebook"
            >
              <Facebook className="h-4 w-4" /> Follow us on Facebook
            </a>
          </div>

          <FooterCol title="Quick Links" links={[
            { to: '/about', label: 'About the Barangay' },
            { to: '/captain', label: 'Punong Barangay Office' },
            { to: '/officials', label: 'Officials' },
            { to: '/announcements', label: 'Announcements' },
            { to: '/projects', label: 'Projects' },
            { to: '/accomplishments', label: 'Accomplishments' },
          ]} />

          <FooterCol title="E-Services" links={[
            { to: '/e-services', label: 'Service Catalog' },
            { to: '/request-document', label: 'Request a Document' },
            { to: '/report-concern', label: 'Report a Concern' },
            { to: '/track-request', label: 'Track Request' },
            { to: '/emergency', label: 'Emergency Contacts' },
            { to: '/contact', label: 'Contact Us' },
          ]} />

          <div className="lg:col-span-3">
            <h4 className="font-heading font-semibold text-sm uppercase tracking-wider opacity-90 mb-4">
              Reach Us
            </h4>
            <ul className="space-y-3 text-sm">
              <li className="flex gap-3"><MapPin className="h-4 w-4 mt-0.5 opacity-80 flex-shrink-0" /><span className="opacity-90">{BARANGAY.address}</span></li>
              <li className="flex gap-3"><Phone className="h-4 w-4 mt-0.5 opacity-80 flex-shrink-0" /><span className="opacity-90">{BARANGAY.hotline}</span></li>
              <li className="flex gap-3"><Mail className="h-4 w-4 mt-0.5 opacity-80 flex-shrink-0" /><span className="opacity-90">{BARANGAY.email}</span></li>
              <li className="flex gap-3"><Clock className="h-4 w-4 mt-0.5 opacity-80 flex-shrink-0" /><div className="opacity-90"><div>{BARANGAY.officeHours}</div><div>{BARANGAY.saturdayHours}</div></div></li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-3 text-xs opacity-80">
          <div>© {year} {BARANGAY.fullName}. All rights reserved.</div>
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-3.5 w-3.5" /> Smart Barangay Action Center
          </div>
          <Link to="/admin/login" className="hover:underline" data-testid="footer-admin-link">
            Admin Portal
          </Link>
        </div>
      </div>
    </footer>
  );
};

const FooterCol = ({ title, links }) => (
  <div className="lg:col-span-2">
    <h4 className="font-heading font-semibold text-sm uppercase tracking-wider opacity-90 mb-4">{title}</h4>
    <ul className="space-y-2.5">
      {links.map((l) => (
        <li key={l.to}>
          <Link to={l.to} className="text-sm opacity-85 hover:opacity-100 hover:text-accent transition" data-testid={`footer-link-${l.to.replace('/', '') || 'home'}`}>
            {l.label}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);
